import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Paper,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { updateIntake, saveIntakeAsync, selectOnboarding } from '../../../store/onboardingSlice';
import { IntakeData } from '../../../api/marketing';

interface ComplianceStepProps {
  onNext: () => void;
  onPrev: () => void;
}

const usStates = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
];

const ComplianceStep: FC<ComplianceStepProps> = ({ onNext, onPrev }) => {
  const dispatch = useAppDispatch();
  const { intake } = useAppSelector(selectOnboarding);

  const [selectedStates, setSelectedStates] = useState<string[]>(
    intake.operatingStates || []
  );
  const [isBonded, setIsBonded] = useState(
    intake.bondingInfo?.isBonded || false
  );

  const { control, handleSubmit, watch } = useForm<Partial<IntakeData>>({
    defaultValues: {
      complianceOfficerName: intake.complianceOfficerName || '',
      complianceOfficerEmail: intake.complianceOfficerEmail || '',
      complianceOfficerPhone: intake.complianceOfficerPhone || '',
      bondingInfo: intake.bondingInfo || {},
    },
  });

  const formValues = watch();

  // Auto-save on change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(updateIntake({
        ...formValues,
        operatingStates: selectedStates,
        bondingInfo: {
          ...formValues.bondingInfo,
          isBonded,
        },
      }));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formValues, selectedStates, isBonded, dispatch]);

  const handleStateToggle = (stateCode: string) => {
    setSelectedStates((prev) =>
      prev.includes(stateCode)
        ? prev.filter((s) => s !== stateCode)
        : [...prev, stateCode]
    );
  };

  const selectAllStates = () => {
    setSelectedStates(usStates.map((s) => s.code));
  };

  const clearAllStates = () => {
    setSelectedStates([]);
  };

  const onSubmit = async (data: Partial<IntakeData>) => {
    const complianceData = {
      ...data,
      operatingStates: selectedStates,
      bondingInfo: {
        ...data.bondingInfo,
        isBonded,
      },
    };
    dispatch(updateIntake(complianceData));
    await dispatch(saveIntakeAsync({ ...intake, ...complianceData, currentStep: 3 }));
    onNext();
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Compliance Details
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Help us configure compliance tracking for your operating states.
      </Typography>

      {/* Operating States */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            States of Operation
          </Typography>
          <Box>
            <Button size="small" onClick={selectAllStates}>
              Select All
            </Button>
            <Button size="small" onClick={clearAllStates}>
              Clear All
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select all states where you conduct collection activities ({selectedStates.length} selected)
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            maxHeight: 200,
            overflowY: 'auto',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {usStates.map((state) => (
            <Chip
              key={state.code}
              label={state.code}
              onClick={() => handleStateToggle(state.code)}
              color={selectedStates.includes(state.code) ? 'primary' : 'default'}
              variant={selectedStates.includes(state.code) ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Paper>
      </Box>

      {/* Bonding Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Bonding Information
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={isBonded}
                onChange={(e) => setIsBonded(e.target.checked)}
              />
            }
            label="Our agency is bonded"
          />
        </FormGroup>

        {isBonded && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <Controller
                name="bondingInfo.amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Bond Amount"
                    placeholder="$100,000"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="bondingInfo.provider"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Surety Provider"
                    placeholder="Surety Company Name"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="bondingInfo.policyNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Policy Number"
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Compliance Officer */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Compliance Officer (Optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Designate a compliance officer to receive compliance alerts and reports
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Controller
              name="complianceOfficerName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Name"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="complianceOfficerEmail"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Email"
                  type="email"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="complianceOfficerPhone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Phone"
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={onPrev}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default ComplianceStep;
